/**
 * STEP — Promise-based confirm dialog.
 * Drop-in replacement for `window.confirm` that uses the app's own ConfirmDialog
 * (translatable, styled, non-blockable) instead of the native browser prompt.
 *
 * Usage:
 *   import { confirm } from "@/components/ui/confirm";
 *   if (!(await confirm({ title: "Delete", description: "Are you sure?" }))) return;
 *
 * Mount <ConfirmHost /> once near the app root (next to <Toaster />).
 */
import { useSyncExternalStore } from "react";
import { ConfirmDialog } from "./dialog";

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "warning" | "default";
}

interface ConfirmState extends ConfirmOptions {
  open: boolean;
  resolve?: (ok: boolean) => void;
}

let state: ConfirmState = { open: false, title: "" };
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const getSnapshot = () => state;

/** Promise-based replacement for window.confirm. Resolves true on confirm. */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    state = { ...options, open: true, resolve };
    emit();
  });
}

function close(result: boolean) {
  state.resolve?.(result);
  state = { ...state, open: false, resolve: undefined };
  emit();
}

export function ConfirmHost() {
  const s = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return (
    <ConfirmDialog
      open={s.open}
      title={s.title}
      description={s.description ?? ""}
      confirmLabel={s.confirmLabel}
      cancelLabel={s.cancelLabel}
      variant={s.variant ?? "danger"}
      onClose={() => close(false)}
      onConfirm={() => close(true)}
    />
  );
}
